import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useIntl } from 'react-intl-next';

import { Popup } from '@atlaskit/popup';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';

import { ResultStatus } from 'src/types/custom-merge-checks';

import { MergeCheckPopup } from '../merge-check-popup';
import { MergeResultsPopupMouseCallbacks } from '../merge-checks-lists/rich-merge-checks-list/hooks';
import { StatusIcon } from '../status-icon';
import statusMessages from '../status-icon.i18n';

import { messages } from './i18n';
import { containerStyles, hoverStyles } from './styled';

export type MergeCheckItemProps = {
  mergeCheckKey: string;
  status: ResultStatus;
  name: string | ReactNode;
  nameAriaLabel: string;
  description?: string | null;
  source?: string;
  updatedOn?: string | Date | null;
  branchName: string;
  trailing?: ReactNode;
  // Name should be truncated if it is UGC
  truncateName?: boolean;
  required: boolean;
  // Below props are only required to show the Popup on hover.
  // The useMergeResultsPopupProps hook can provide these.
  popupIsOpen?: boolean;
  showPopup?: (key?: string) => void;
  hidePopup?: () => void;
  mouseCallbacks?: MergeResultsPopupMouseCallbacks;
};

/**
 * MergeCheckItem can be used to render both the classic and custom merge checks.
 *
 * @param mergeCheckKey - The key of the merge check.
 * @param status - The status of the merge check.
 * @param name - The name of the merge check.
 * @param description - The description of the merge check.
 * @param updatedOn - The last updated date of the merge check.
 * @param branchName - The destination branch of the merge check target.
 * @param trailing - The trailing component for the merge check. This allows custom merge checks to
 *  render a trailing component, such as a retry button.
 * @param required - Whether the merge check is required.
 */
export const MergeCheckItem = ({
  mergeCheckKey,
  status,
  name,
  nameAriaLabel,
  description,
  source,
  updatedOn,
  branchName,
  trailing,
  truncateName = false,
  required,
  popupIsOpen = false,
  showPopup,
  hidePopup,

  mouseCallbacks: {
    onMouseEnterPopup,
    onMouseLeavePopup,
    onMouseEnterTrigger,
    onMouseLeaveTrigger,
  } = {
    onMouseEnterPopup: () => undefined,
    onMouseLeavePopup: () => undefined,
    onMouseEnterTrigger: () => undefined,
    onMouseLeaveTrigger: () => undefined,
  },
}: MergeCheckItemProps) => {
  const { formatMessage } = useIntl();
  const focusElement = useRef<HTMLWebViewElement>(null);

  const onMouseEnterEvent = useCallback(() => {
    onMouseEnterTrigger?.(mergeCheckKey);
  }, [mergeCheckKey, onMouseEnterTrigger]);

  const onMouseLeaveEvent = useCallback(() => {
    onMouseLeaveTrigger?.(mergeCheckKey);
  }, [mergeCheckKey, onMouseLeaveTrigger]);

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      // Dismiss the popup when 'Escape' is pressed by unfocusing
      if (event.key === 'Escape' && popupIsOpen) {
        if (document.activeElement === focusElement.current) {
          focusElement.current?.blur();
        }

        // This bit dismisses the popup when the retry button is focused
        if (trailing) {
          focusElement.current?.querySelector('button')?.blur();
        }
      }
    },
    [popupIsOpen, trailing]
  );

  useEffect(() => {
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('keydown', onEscape);
    };
  }, [onEscape]);

  const onFocus = useCallback(
    (key?: string) => {
      showPopup?.(key);
    },
    [showPopup]
  );

  const onBlur = useCallback(() => {
    hidePopup?.();
  }, [hidePopup]);

  const statusAriaLabel = useMemo(() => {
    switch (status) {
      case 'PENDING':
      case 'IN-PROGRESS':
        return formatMessage(statusMessages.inProgressIconLabel);
      case 'IMPENDING':
        return formatMessage(statusMessages.impendingIconLabel);
      case 'FAILED':
      case 'ERROR':
        return formatMessage(statusMessages.failedIconLabel);
      case 'SUCCESS':
        return formatMessage(statusMessages.successIconLabel);
      default:
        return '';
    }
  }, [formatMessage, status]);

  return (
    <Popup
      placement="left"
      content={contentProps => (
        <MergeCheckPopup
          mergeCheckKey={mergeCheckKey}
          status={status}
          name={name}
          description={description}
          source={source}
          updatedOn={updatedOn}
          branchName={branchName}
          required={required}
          onMouseEnter={onMouseEnterPopup}
          onMouseLeave={onMouseLeavePopup}
          {...contentProps}
        />
      )}
      autoFocus={false}
      shouldRenderToParent
      shouldDisableFocusLock
      isOpen={popupIsOpen}
      trigger={triggerProps => (
        <Box
          testId="mergeCheckItem"
          xcss={[containerStyles, popupIsOpen && hoverStyles]}
          onMouseEnter={onMouseEnterEvent}
          onMouseLeave={onMouseLeaveEvent}
          {...triggerProps}
        >
          {/* Need an inner Box to handle tab focus since the outer Box ref is used by the Popup and is inaccessible */}
          <Box
            testId="mergeCheckItemFocusElement"
            ref={focusElement}
            // Make this tabbable if it's a non-successful result
            tabIndex={0}
            aria-label={formatMessage(messages.checkAriaLabel, {
              name: nameAriaLabel,
              status: statusAriaLabel,
              message: description
                ? formatMessage(messages.checkAriaLabelMessageSegment, {
                    message: description,
                  })
                : '',
            })}
            xcss={xcss({ outlineColor: 'color.border.focused' })}
            onFocus={() => onFocus(mergeCheckKey)}
            onBlur={onBlur}
          >
            <Flex direction="row" gap="space.100" alignItems="center">
              <StatusIcon
                status={status}
                required={required}
                testIdPrefix="mergeCheckItemIcon"
              />
              <Text maxLines={truncateName ? 2 : 0}>{name}</Text>
              {trailing && <div>{trailing}</div>}
            </Flex>
          </Box>
        </Box>
      )}
    />
  );
};
